import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useDebugLayer } from './DebugLayerProvider';
import { useDebugCapability } from './useDebugCapability';
import { PreviewFiltersSection } from './sections/PreviewFiltersSection';
import { ActionsSection } from './sections/ActionsSection';
import { EntriesSection } from './sections/EntriesSection';

/**
 * Konsolenartiges, NICHT-modales Debug-Panel.
 *
 * Verhalten:
 *  - keine globale Verdunkelung, kein Radix Dialog/Overlay
 *  - sitzt unten links über dem App-UI (Float, nicht Drawer)
 *  - kompakt: max. 70vh × 520px breit
 *  - Klick ausserhalb schliesst die Konsole, blockiert dabei aber
 *    keine Klicks im App-UI (kein vorgelagerter Overlay-Layer)
 *  - rollen-gegated wie der Button
 */
export function DebugLayerPanel() {
  const { canUseDebugLayer } = useDebugCapability();
  const { open, setOpen, routeEntries, route, badgeCount } = useDebugLayer();
  const ref = useRef<HTMLDivElement | null>(null);

  // Outside-Click → schliessen, OHNE einen blockierenden Overlay-Layer
  // einzuziehen. So bleibt die App in vollem Umfang bedienbar.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(ev: PointerEvent) {
      const el = ref.current;
      const target = ev.target as Node | null;
      if (!el || !target) return;
      // Floating-Button selbst soll Klicks behalten (Toggle-Logik).
      const btn = document.querySelector('[data-testid="debug-floating-button"]');
      if (btn && btn.contains(target)) return;
      if (!el.contains(target)) setOpen(false);
    }
    window.addEventListener('pointerdown', onPointerDown, true);
    return () => window.removeEventListener('pointerdown', onPointerDown, true);
  }, [open, setOpen]);

  // ESC schliesst die Konsole — Standardverhalten für Dev-Tools.
  useEffect(() => {
    if (!open) return;
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  if (!canUseDebugLayer || !open) return null;

  return (
    <div
      ref={ref}
      role="complementary"
      aria-label="Debug Console"
      data-testid="debug-layer-panel"
      className={[
        // gedockt unten links, oberhalb des Floating-Buttons
        'fixed left-4 bottom-16 z-[150]',
        'w-[420px] max-w-[calc(100vw-2rem)] max-h-[min(70vh,560px)]',
        'flex flex-col',
        'rounded-xl border border-border bg-card shadow-xl',
        'overflow-hidden pointer-events-auto',
        // Mobile: leicht angepasste Breite, kein Fullscreen, keine
        // App-weite Abdunkelung.
        'sm:left-4 sm:right-auto',
      ].join(' ')}
    >
      <header className="flex items-start justify-between gap-3 px-3 py-2 border-b border-border/60">
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-tight">Debug Console</p>
          <p className="text-[11px] text-muted-foreground font-mono truncate">
            {route || '/'}
            {badgeCount > 0 ? ` · ${badgeCount} Hinweise` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Debug Console schliessen"
          className="h-6 w-6 inline-flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto">
        <PreviewFiltersSection />
        <ActionsSection />
        <EntriesSection
          title="Fehler"
          description="Technische Details werden ausschliesslich hier angezeigt — nicht im normalen User-UI."
          entries={routeEntries}
          section="errors"
        />
        <EntriesSection
          title="Debug Informationen"
          entries={routeEntries}
          section="debug"
        />
        <EntriesSection
          title="Technische Metadaten"
          entries={routeEntries}
          section="metadata"
        />
      </div>
    </div>
  );
}
